import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    DialogContentText,
    Tab,
    Tabs,
    Grid,
    Card,
    CardContent,
    CardActions,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useUser } from '../components/userContext';
import { useNavigate } from 'react-router-dom';
import { useThemeContext, THEME_CLASSIC } from '../theme/ThemeContext';
import { useSnackbar } from 'notistack';

// Icons
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import BugReportIcon from '@mui/icons-material/BugReport';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DirectionsIcon from '@mui/icons-material/Directions';
import RefreshIcon from '@mui/icons-material/Refresh';
import { HelpIconButton } from './HelpInfoSystem';

// DRK Komponenten
import {
    DrkPageContainer,
    DrkCard,
    DrkInfoBox,
    DrkActionBar,
    DrkDialog,
    DrkDialogCard,
    DrkHeaderButtonGroup,
    DrkHeaderIconButton
} from '../components/DrkComponents';

// Event für die Benachrichtigung über Änderungen an ungelesenen Nachrichten
export const NOTIFICATION_READ_EVENT = 'notification_read_update';

/**
 * NotificationsPage - Hauptseite für Benutzerbenachrichtigungen
 */
function NotificationsPage() {
    const { user } = useUser();
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const refreshTimerRef = useRef(null);

    // State für Benachrichtigungen
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // State für Filterung und Paginierung
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // State für Filter-Menü
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [selectedEventTypes, setSelectedEventTypes] = useState([]);

    // State für Aktions-Menü
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [selectedNotification, setSelectedNotification] = useState(null);

    // State für Bestätigungsdialoge
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState(null);
    const [confirmDialogMessage, setConfirmDialogMessage] = useState('');

    // Benachrichtigungen laden
    const fetchNotifications = useCallback(async (isManualRefresh = false) => {
        try {
            if (isManualRefresh) {
                setIsRefreshing(true);
            } else {
                setIsLoading(true);
            }
            setError(null);

            // Parameter für die API-Anfrage
            const offset = (currentPage - 1) * itemsPerPage;
            let queryParams = new URLSearchParams();

            queryParams.append('limit', itemsPerPage.toString());
            queryParams.append('offset', offset.toString());

            // Filter nach Lesestatus
            if (activeTab === 'unread') {
                queryParams.append('unreadOnly', 'true');
            } else if (activeTab === 'read') {
                queryParams.append('readOnly', 'true'); // Neuer Parameter für gelesene Nachrichten
            }

            // Event-Typ Filter
            if (selectedEventTypes.length === 1) {
                queryParams.append('eventType', selectedEventTypes[0]);
            }

            // API-Aufruf
            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/user-notifications?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Fehler beim Abrufen der Benachrichtigungen');
            }

            const result = await response.json();

            // Daten setzen
            setNotifications(result.data.notifications);
            setTotalCount(result.data.total);
            setUnreadCount(result.data.unreadCount);

            if (isManualRefresh) {
                enqueueSnackbar('Benachrichtigungen aktualisiert', { variant: 'success' });
            }
        } catch (err) {
            console.error('Fehler beim Laden der Benachrichtigungen:', err);
            setError('Beim Laden der Benachrichtigungen ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [user, currentPage, activeTab, selectedEventTypes, itemsPerPage, enqueueSnackbar]);

    // Manuelles Aktualisieren der Benachrichtigungen
    const handleRefresh = () => {
        fetchNotifications(true);
    };

    // Event-Typen und deren Konfiguration
    const eventTypeConfig = useMemo(() => ({
        // Transport-bezogene Events
        'transportBooked': {
            label: 'Transport gebucht',
            icon: <LocalShippingIcon />,
            color: '#4caf50', // Grün
            buttonText: 'Zum Transport',
            buttonIcon: <VisibilityIcon />,
        },
        'transportCanceled': {
            label: 'Transport storniert',
            icon: <LocalShippingIcon />,
            color: '#f44336', // Rot
            buttonText: 'Zum Transport',
            buttonIcon: <VisibilityIcon />,
        },
        'transportRescheduled': {
            label: 'Transport umgeplant',
            icon: <EventIcon />,
            color: '#ff9800', // Orange
            buttonText: 'Zum Transport',
            buttonIcon: <VisibilityIcon />,
        },
        'transportDelay': {
            label: 'Transport verspätet',
            icon: <ErrorOutlineIcon />,
            color: '#ff9800', // Orange
            buttonText: 'Zum Transport',
            buttonIcon: <VisibilityIcon />,
        },
        'transportVehicleStart': {
            label: 'Fahrzeug unterwegs',
            icon: <LocalShippingIcon />,
            color: '#2196f3', // Blau
            buttonText: 'Zum Transport',
            buttonIcon: <VisibilityIcon />,
        },
        'transportVehicleArrived': {
            label: 'Fahrzeug angekommen',
            icon: <LocalShippingIcon />,
            color: '#2196f3', // Blau
            buttonText: 'Zum Transport',
            buttonIcon: <VisibilityIcon />,
        },

        // System-Events
        'newBugReported': {
            label: 'Neuer Bug-Report',
            icon: <BugReportIcon />,
            color: '#9c27b0', // Lila
            buttonText: 'Zum Bug', // Wird dynamisch überschrieben je nach Typ
            buttonIcon: <VisibilityIcon />,
        },
        'newsletter': {
            label: 'Newsletter',
            icon: <EmailIcon />,
            color: '#607d8b', // Blaugrau
            buttonText: 'Zum Newsletter',
            buttonIcon: <VisibilityIcon />,
        },
        'systemMessages': {
            label: 'Systembenachrichtigung',
            icon: <InfoIcon />,
            color: '#607d8b', // Blaugrau
            buttonText: 'Mehr Info',
            buttonIcon: <InfoIcon />,
        }
    }), []);

    // Auto-Refresh-Effekt: Daten alle 30 Sekunden aktualisieren
    useEffect(() => {
        if (user?.user_id) {
            // Initial fetch
            fetchNotifications();

            // Starte das Auto-Refresh-Intervall
            refreshTimerRef.current = setInterval(() => {
                fetchNotifications();
            }, 30000); // 30 Sekunden

            // Aufräumen bei Unmount
            return () => {
                if (refreshTimerRef.current) {
                    clearInterval(refreshTimerRef.current);
                }
            };
        }
    }, [user, fetchNotifications]);

    // Bei Änderungen der Filter/Paginierung neu laden
    useEffect(() => {
        if (user?.user_id) {
            fetchNotifications();
        }
    }, [user, activeTab, currentPage, selectedEventTypes, fetchNotifications]);

    // Benachrichtigung als gelesen markieren
    const markAsRead = async (notificationId) => {
        try {
            setIsLoading(true);

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/user-notifications/${notificationId}/read`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Fehler beim Markieren der Benachrichtigung als gelesen');
            }

            // Aktualisiere den lokalen State
            setNotifications(prev =>
                prev.map(notification =>
                    notification.notification_id === notificationId
                        ? { ...notification, is_read: true, read_at: new Date().toISOString() }
                        : notification
                )
            );

            // Ungelesene Anzahl reduzieren
            setUnreadCount(prev => Math.max(0, prev - 1));

            // Benachrichtige andere Komponenten über die Änderung
            const event = new CustomEvent(NOTIFICATION_READ_EVENT, {
                detail: { unreadCount: Math.max(0, unreadCount - 1) }
            });
            window.dispatchEvent(event);

            enqueueSnackbar('Benachrichtigung als gelesen markiert', { variant: 'success' });
        } catch (err) {
            console.error('Fehler beim Markieren als gelesen:', err);
            enqueueSnackbar('Fehler beim Markieren als gelesen', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Alle Benachrichtigungen als gelesen markieren
    const markAllAsRead = async () => {
        try {
            setIsLoading(true);

            // Event-Typ Filter
            let eventTypeParam = '';
            if (selectedEventTypes.length === 1) {
                eventTypeParam = `?eventType=${selectedEventTypes[0]}`;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/user-notifications/read-all${eventTypeParam}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Fehler beim Markieren aller Benachrichtigungen als gelesen');
            }

            const result = await response.json();

            // Alle im State als gelesen markieren
            if (activeTab === 'all' || activeTab === 'unread') {
                setNotifications(prev =>
                    prev.map(notification => ({
                        ...notification,
                        is_read: true,
                        read_at: new Date().toISOString()
                    }))
                );
            }

            // Ungelesene Anzahl auf 0 setzen
            setUnreadCount(0);

            // Benachrichtige andere Komponenten über die Änderung
            const event = new CustomEvent(NOTIFICATION_READ_EVENT, {
                detail: { unreadCount: 0 }
            });
            window.dispatchEvent(event);

            enqueueSnackbar(`${result.data.count} Benachrichtigung(en) als gelesen markiert`, { variant: 'success' });

            // Wenn wir auf dem "Ungelesen"-Tab waren, Daten neu laden
            if (activeTab === 'unread') {
                fetchNotifications();
            }
        } catch (err) {
            console.error('Fehler beim Markieren aller als gelesen:', err);
            enqueueSnackbar('Fehler beim Markieren aller als gelesen', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Benachrichtigung löschen
    const deleteNotification = async (notificationId) => {
        try {
            setIsLoading(true);

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/user-notifications/${notificationId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Fehler beim Löschen der Benachrichtigung');
            }

            // Aus dem lokalen State entfernen
            setNotifications(prev =>
                prev.filter(notification => notification.notification_id !== notificationId)
            );

            // Gesamtzahl reduzieren
            setTotalCount(prev => Math.max(0, prev - 1));

            // Wenn die Nachricht ungelesen war, die ungelesene Anzahl reduzieren
            const wasUnread = notifications.find(n => n.notification_id === notificationId)?.is_read === false;
            if (wasUnread) {
                const newUnreadCount = Math.max(0, unreadCount - 1);
                setUnreadCount(newUnreadCount);

                // Benachrichtige andere Komponenten über die Änderung
                const event = new CustomEvent(NOTIFICATION_READ_EVENT, {
                    detail: { unreadCount: newUnreadCount }
                });
                window.dispatchEvent(event);
            }

            enqueueSnackbar('Benachrichtigung erfolgreich gelöscht', { variant: 'success' });
        } catch (err) {
            console.error('Fehler beim Löschen:', err);
            enqueueSnackbar('Fehler beim Löschen der Benachrichtigung', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Alle Benachrichtigungen löschen
    const deleteAllNotifications = async () => {
        try {
            setIsLoading(true);

            // Event-Typ Filter und "Nur gelesene" Filter
            let params = [];

            if (selectedEventTypes.length === 1) {
                params.push(`eventType=${selectedEventTypes[0]}`);
            }

            if (activeTab === 'read') {
                params.push('readOnly=true');
            }

            const queryString = params.length ? `?${params.join('&')}` : '';

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/api/user-notifications${queryString}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Fehler beim Löschen aller Benachrichtigungen');
            }

            const result = await response.json();

            // Benachrichtigungen aus dem State entfernen
            if (activeTab === 'all') {
                setNotifications([]);
                setTotalCount(0);
                setUnreadCount(0);

                // Benachrichtige andere Komponenten über die Änderung
                const event = new CustomEvent(NOTIFICATION_READ_EVENT, {
                    detail: { unreadCount: 0 }
                });
                window.dispatchEvent(event);
            } else if (activeTab === 'unread') {
                setNotifications([]);
                setTotalCount(prev => prev - result.data.count);
                setUnreadCount(0);

                // Benachrichtige andere Komponenten über die Änderung
                const event = new CustomEvent(NOTIFICATION_READ_EVENT, {
                    detail: { unreadCount: 0 }
                });
                window.dispatchEvent(event);
            } else if (activeTab === 'read') {
                // Für den "Gelesen"-Tab nur gelesene entfernen
                setNotifications([]);
                setTotalCount(prev => prev - result.data.count);
            }

            enqueueSnackbar(`${result.data.count} Benachrichtigung(en) erfolgreich gelöscht`, { variant: 'success' });
        } catch (err) {
            console.error('Fehler beim Löschen aller Benachrichtigungen:', err);
            enqueueSnackbar('Fehler beim Löschen aller Benachrichtigungen', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Benachrichtigungsdetails anzeigen und nur als gelesen markieren
    const handleNotificationClick = (notification) => {
        // Nur als gelesen markieren, wenn noch nicht gelesen - keine Navigation!
        if (!notification.is_read) {
            markAsRead(notification.notification_id);
        }
    };

    // Behandelt Klicks auf den Detail-Button
    const handleDetailButtonClick = (event, notification) => {
        event.stopPropagation(); // Verhindert, dass der Klick die ganze Karte aktiviert

        // Wenn die Benachrichtigung noch nicht gelesen ist, als gelesen markieren
        if (!notification.is_read) {
            markAsRead(notification.notification_id);
        }

        /*
        // Verwende die detail_url aus den Daten, wenn vorhanden
        if (notification.data?.detail_url) {
            window.open(notification.data.detail_url, '_blank');
            return;
        }*/

        // Fallback: Je nach Event-Typ zur entsprechenden Seite navigieren
        if (notification.event_type.startsWith('transport')) {
            const transportId = notification.data?.transport_id;
            if (transportId) {
                navigate(`/transport/${transportId}`);
            }
        } else if (notification.event_type === 'newBugReported') {
            navigate('/bug-reports'); // Immer zur Übersichtsseite für Bugs
        }
    };

    // Event-Typ Icon und Farbe
    const getEventTypeInfo = (eventType) => {
        const defaultInfo = {
            icon: <NotificationsIcon />,
            color: '#9e9e9e',
            label: eventType,
            buttonText: 'Details',
            buttonIcon: <InfoIcon />
        };

        return eventTypeConfig[eventType] || defaultInfo;
    };

    // Filter Menü öffnen/schließen
    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    // Event-Typ Filter ändern
    const handleEventTypeFilter = (eventType) => {
        // Wenn bereits ausgewählt, entfernen, sonst hinzufügen
        if (selectedEventTypes.includes(eventType)) {
            setSelectedEventTypes(prev => prev.filter(type => type !== eventType));
        } else {
            setSelectedEventTypes([eventType]); // Wir erlauben nur einen Event-Typ
        }
    };

    // Tabs wechseln
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setCurrentPage(1); // Zurück zur ersten Seite
    };

    // Aktions-Menü öffnen/schließen
    const handleActionMenuOpen = (event, notification) => {
        event.stopPropagation(); // Verhindert Bubbling zum Notification-Klick
        setActionAnchorEl(event.currentTarget);
        setSelectedNotification(notification);
    };

    const handleActionMenuClose = () => {
        setActionAnchorEl(null);
    };

    // Bestätigungsdialog öffnen
    const openConfirmDialog = (action, message) => {
        setConfirmDialogAction(action);
        setConfirmDialogMessage(message);
        setConfirmDialogOpen(true);
        handleActionMenuClose(); // Aktions-Menü schließen
    };

    // Bestätigungsdialog bestätigen
    const handleConfirmAction = async () => {
        try {
            if (confirmDialogAction === 'deleteAll') {
                await deleteAllNotifications();
            } else if (confirmDialogAction === 'markAllRead') {
                await markAllAsRead();
            } else if (confirmDialogAction === 'deleteOne' && selectedNotification) {
                await deleteNotification(selectedNotification.notification_id);
            }
        } catch (error) {
            console.error("Fehler bei der Ausführung der Aktion:", error);
            enqueueSnackbar('Fehler bei der Ausführung der Aktion', { variant: 'error' });
        } finally {
            setConfirmDialogOpen(false);
            setConfirmDialogAction(null);
            setSelectedNotification(null); // Ausgewählte Benachrichtigung zurücksetzen
        }
    };

    // Suchbegriff ändern
    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);

        // Lokal nach dem Suchbegriff filtern - fürs Erste ist das okay
        // Bei größeren Datenmengen sollte die Suche am Server erfolgen
    };

    // Benachrichtigungen gemäß Suchbegriff filtern
    const filteredNotifications = useMemo(() => {
        if (!searchTerm) return notifications;

        return notifications.filter(notification => {
            const searchLower = searchTerm.toLowerCase();
            const titleMatches = notification.title?.toLowerCase().includes(searchLower);
            const messageMatches = notification.message?.toLowerCase().includes(searchLower);
            const typeMatches = getEventTypeInfo(notification.event_type).label.toLowerCase().includes(searchLower);

            return titleMatches || messageMatches || typeMatches;
        });
        // eslint-disable-next-line
    }, [notifications, searchTerm]);

    /**
     * Formatiert zusätzliche Informationen aus den Daten der Benachrichtigung
     * @param {Object} notification - Die Benachrichtigung
     * @returns {JSX.Element} - Formatierte Zusatzinformationen
     */
    const renderAdditionalInfo = (notification) => {
        const { event_type, data } = notification;

        if (!data) return null;

        // Gemeinsame Zusatzinformationen nach Typ aufbereiten
        if (event_type.startsWith('transport')) {
            return (
                <Box sx={{ mt: 2, ml: 7, mb: 1 }}>
                    {data.patient_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Patient: {data.patient_name}
                            </Typography>
                        </Box>
                    )}

                    {(data.from_location || data.to_location) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <DirectionsIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                {data.from_location && `Von: ${data.from_location}`}
                                {data.from_location && data.to_location && ` → `}
                                {data.to_location && `Nach: ${data.to_location}`}
                            </Typography>
                        </Box>
                    )}

                    {data.date && data.time && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Termin: {data.date} um {data.time} Uhr
                            </Typography>
                        </Box>
                    )}

                    {event_type === 'transportRescheduled' && data.reason && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <InfoIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Grund: {data.reason}
                            </Typography>
                        </Box>
                    )}

                    {event_type === 'transportDelay' && data.delayMinutes && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Verzögerung: {data.delayMinutes} Minuten
                            </Typography>
                        </Box>
                    )}

                    {event_type === 'transportVehicleStart' && data.estimated_arrival && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Voraussichtliche Ankunft: {data.estimated_arrival} Uhr
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        } else if (event_type === 'newBugReported') {
            const bugType = data.custom_fields?.type === 'wish' ? 'Verbesserungswunsch' : 'Bug';

            return (
                <Box sx={{ mt: 2, ml: 7, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <ErrorIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            Typ: {bugType}
                        </Typography>
                    </Box>

                    {data.priority && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PriorityHighIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Priorität: {data.priority}
                            </Typography>
                        </Box>
                    )}

                    {data.assignee && data.assignee !== 'Noch nicht zugewiesen' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Zugewiesen an: {data.assignee}
                            </Typography>
                        </Box>
                    )}

                    {data.reporter && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Gemeldet von: {data.reporter}
                            </Typography>
                        </Box>
                    )}

                    {data.created_at && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Erstellt am: {data.created_at}
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        }

        return null;
    };

    // Benachrichtigungskarte rendern
    const renderNotificationCard = (notification) => {
        const { icon, color, label, buttonText, buttonIcon } = getEventTypeInfo(notification.event_type);
        const isUnread = !notification.is_read;

        // Formatierung des Datums
        const formattedDate = new Date(notification.created_at).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        // Bestimme, ob ein Link-Button angezeigt werden soll
        const shouldShowLinkButton = notification.data?.detail_url ||
            notification.event_type.startsWith('transport') ||
            notification.event_type === 'newBugReported';

        // ID des betroffenen Elements (für den Button-Tooltip)
        let itemId = '';
        if (notification.event_type.startsWith('transport') && notification.data?.transport_id) {
            itemId = `#${notification.data.transport_id}`;
        } else if (notification.event_type === 'newBugReported' && notification.data?.bug_id) {
            itemId = `#${notification.data.bug_id}`;
        }

        // Spezielle Anpassung des Button-Textes für Bug/Verbesserungswunsch
        let customButtonText = buttonText;
        if (notification.event_type === 'newBugReported') {
            const isWish = notification.data?.custom_fields?.type === 'wish';
            customButtonText = isWish ? 'Zum Verbesserungswunsch' : 'Zum Bug';
        }

        return (
            <Card
                key={notification.notification_id}
                sx={{
                    mb: 2,
                    borderLeft: isUnread ? `4px solid ${color}` : 'none',
                    borderRadius: isClassic ? 2 : 3,
                    boxShadow: isUnread ? 2 : 1,
                    backgroundColor: (theme) => {
                        if (!isUnread) return theme.palette.background.paper;
                        const isDark = theme.palette.mode === 'dark';
                        return isClassic ? (isDark ? 'rgba(66,165,245,0.15)' : '#f5f9ff') : (isDark ? 'rgba(255,152,0,0.15)' : 'rgba(255,152,0,0.1)');
                    },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                    },
                    cursor: 'pointer'
                }}
                onClick={() => handleNotificationClick(notification)}
            >
                <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: `${color}15`, // 15% Opazität
                                    color: color,
                                    mr: 2
                                }}
                            >
                                {icon}
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: isUnread ? 'bold' : 'medium' }}>
                                    {notification.title}
                                    {itemId && (
                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                            {itemId}
                                        </Typography>
                                    )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {label} • {formattedDate}
                                </Typography>
                            </Box>
                        </Box>

                        <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, notification)}
                            sx={{ ml: 1 }}
                            aria-label="Aktionen"
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Typography variant="body2" sx={{ mt: 1, mx: 7, color: isUnread ? 'text.primary' : 'text.secondary' }}>
                        {notification.message}
                    </Typography>

                    {/* Zusätzliche formatierte Informationen */}
                    {renderAdditionalInfo(notification)}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pt: 0, pb: 1 }}>
                    {!notification.is_read && (
                        <Button
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.notification_id);
                            }}
                        >
                            Als gelesen markieren
                        </Button>
                    )}

                    {/* Link-Button zu Details */}
                    {shouldShowLinkButton && (
                        <Tooltip title={itemId ? `${customButtonText} ${itemId}` : customButtonText}>
                            <Button
                                size="small"
                                color="primary"
                                variant="outlined"
                                startIcon={buttonIcon}
                                onClick={(e) => handleDetailButtonClick(e, notification)}
                                sx={{ ml: 1 }}
                            >
                                {customButtonText}
                            </Button>
                        </Tooltip>
                    )}
                </CardActions>
            </Card>
        );
    };

    // Liste der Benachrichtigungen rendern
    const renderNotificationsList = () => {
        if (isLoading && notifications.length === 0) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <DrkInfoBox color="error" sx={{ my: 3 }}>
                    <Typography color="error">{error}</Typography>
                </DrkInfoBox>
            );
        }

        if (filteredNotifications.length === 0) {
            let message = '';

            if (searchTerm) {
                message = 'Keine Benachrichtigungen entsprechen Ihrem Suchbegriff.';
            } else if (activeTab === 'all') {
                message = 'Sie haben keine Benachrichtigungen.';
            } else if (activeTab === 'unread') {
                message = 'Sie haben keine ungelesenen Benachrichtigungen.';
            } else if (activeTab === 'read') {
                message = 'Sie haben keine gelesenen Benachrichtigungen.';
            }

            return (
                <DrkInfoBox sx={{ my: 3, py: 6 }}>
                    <NotificationsOffIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6">{message}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Benachrichtigungen werden angezeigt, sobald neue Ereignisse verfügbar sind.
                    </Typography>
                </DrkInfoBox>
            );
        }

        return (
            <Box>
                {filteredNotifications.map(notification => renderNotificationCard(notification))}

                {/* Lademeldung beim Nachladen */}
                {isLoading && notifications.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <CircularProgress size={30} />
                    </Box>
                )}

                {/* Paginierung */}
                {totalCount > itemsPerPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                        <Button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1 || isLoading}
                            sx={{ mx: 1 }}
                        >
                            Zurück
                        </Button>
                        <Typography sx={{ alignSelf: 'center', mx: 2 }}>
                            Seite {currentPage} von {Math.ceil(totalCount / itemsPerPage)}
                        </Typography>
                        <Button
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={currentPage >= Math.ceil(totalCount / itemsPerPage) || isLoading}
                            sx={{ mx: 1 }}
                        >
                            Weiter
                        </Button>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <DrkPageContainer>
            <Box sx={{ position: 'relative' }}>
                {/* Header Button Group with Setup and Help buttons */}
                <DrkHeaderButtonGroup
                    position="topRight"
                    buttons={[
                        <DrkHeaderIconButton
                            key="refresh"
                            bgColor="#f0f0f0"  // Hintergrund
                            color="#555555"    // Icon-Farbe
                            icon={<RefreshIcon />}
                            tooltip="Benachrichtigungen aktualisieren"
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                        />,
                        <DrkHeaderIconButton
                            key="settings"
                            icon={<SettingsIcon />}
                            tooltip="Benachrichtigungseinstellungen"
                            onClick={() => navigate('/notification-preferences')}
                        />,
                        <HelpIconButton
                            key="help"
                            pageId="notifications"
                            position="inline"
                        />
                    ]}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" gutterBottom>
                        Benachrichtigungen
                    </Typography>
                    {isRefreshing && <CircularProgress size={24} />}
                </Box>

                <Typography variant="body1" paragraph>
                    Hier finden Sie alle Ihre Benachrichtigungen. Klicken Sie auf eine Benachrichtigung, um sie zu öffnen.
                </Typography>

                {/* Aktionsleiste mit Suche und Filterung */}
                <DrkCard elevation={isClassic ? 2 : 0} sx={{ maxWidth: '100%', mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        {/* Suchfeld und Filterung */}
                        <Grid item xs={12} md={8}>
                            <DrkActionBar
                                searchTerm={searchTerm}
                                onSearchChange={handleSearchChange}
                                searchPlaceholder="Benachrichtigungen durchsuchen..."
                                sx={{ mb: { xs: 1, md: 0 } }}
                            />
                        </Grid>

                        {/* Aktionsbuttons */}
                        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<FilterListIcon />}
                                    onClick={handleFilterClick}
                                    disabled={isLoading}
                                >
                                    Filter
                                    {selectedEventTypes.length > 0 && (
                                        <Chip
                                            label={selectedEventTypes.length}
                                            size="small"
                                            sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Button>

                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<DeleteIcon />}
                                    disabled={isLoading || filteredNotifications.length === 0}
                                    onClick={() => openConfirmDialog(
                                        'deleteAll',
                                        'Möchten Sie wirklich alle Benachrichtigungen löschen?'
                                    )}
                                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                                >
                                    Alle löschen
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CheckCircleIcon />}
                                    disabled={isLoading || unreadCount === 0}
                                    onClick={() => openConfirmDialog(
                                        'markAllRead',
                                        'Möchten Sie alle Benachrichtigungen als gelesen markieren?'
                                    )}
                                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                                >
                                    Alle gelesen
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Tabs für Alle/Ungelesen/Gelesen */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant={isMobile ? "fullWidth" : "standard"}
                            sx={{ minHeight: 48 }}
                        >
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <NotificationsIcon sx={{ mr: 1, fontSize: 20 }} />
                                        Alle ({totalCount})
                                    </Box>
                                }
                                value="all"
                            />
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <NotificationsActiveIcon sx={{ mr: 1, fontSize: 20 }} />
                                        Ungelesen ({unreadCount})
                                    </Box>
                                }
                                value="unread"
                            />
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <NotificationsOffIcon sx={{ mr: 1, fontSize: 20 }} />
                                        Gelesen ({totalCount - unreadCount})
                                    </Box>
                                }
                                value="read"
                            />
                        </Tabs>
                    </Box>

                    {/* Ausgewählte Filter anzeigen */}
                    {selectedEventTypes.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {selectedEventTypes.map(eventType => {
                                const { label, color } = getEventTypeInfo(eventType);
                                return (
                                    <Chip
                                        key={eventType}
                                        label={label}
                                        onDelete={() => handleEventTypeFilter(eventType)}
                                        sx={{
                                            bgcolor: `${color}10`, // 10% Opazität
                                            color: color,
                                            borderColor: color
                                        }}
                                        variant="outlined"
                                    />
                                );
                            })}
                            <Chip
                                label="Filter zurücksetzen"
                                onClick={() => setSelectedEventTypes([])}
                                color="default"
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    )}
                </DrkCard>

                {/* Liste der Benachrichtigungen */}
                <Box sx={{ mt: 2 }}>
                    {renderNotificationsList()}
                </Box>
            </Box>

            {/* Filter-Menü */}
            <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleFilterClose}
                PaperProps={{
                    style: {
                        maxHeight: 'calc(100vh - 200px)',
                        width: '250px',
                    },
                }}
            >
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
                    Nach Event-Typ filtern
                </Typography>
                <Divider />

                {Object.entries(eventTypeConfig).map(([eventType, config]) => (
                    <MenuItem
                        key={eventType}
                        onClick={() => handleEventTypeFilter(eventType)}
                        selected={selectedEventTypes.includes(eventType)}
                    >
                        <ListItemIcon sx={{ color: config.color }}>
                            {config.icon}
                        </ListItemIcon>
                        <ListItemText primary={config.label} />
                    </MenuItem>
                ))}
            </Menu>

            {/* Aktions-Menü für einzelne Benachrichtigung */}
            <Menu
                anchorEl={actionAnchorEl}
                open={Boolean(actionAnchorEl)}
                onClose={handleActionMenuClose}
            >
                {selectedNotification && !selectedNotification.is_read && (
                    <MenuItem onClick={() => {
                        markAsRead(selectedNotification.notification_id);
                        handleActionMenuClose();
                    }}>
                        <ListItemIcon>
                            <CheckCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Als gelesen markieren" />
                    </MenuItem>
                )}

                <MenuItem onClick={() => {
                    handleActionMenuClose(); // Menü schließen, bevor der Dialog geöffnet wird
                    openConfirmDialog(
                        'deleteOne',
                        'Möchten Sie diese Benachrichtigung wirklich löschen?'
                    );
                }}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Löschen" />
                </MenuItem>
            </Menu>

            {/* Bestätigungsdialog */}
            <DrkDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                title="Bestätigung"
                actions={
                    <>
                        <Button onClick={() => setConfirmDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleConfirmAction}
                            variant="contained"
                            color={confirmDialogAction === 'deleteAll' || confirmDialogAction === 'deleteOne' ? 'secondary' : 'primary'}
                        >
                            Bestätigen
                        </Button>
                    </>
                }
            >
                <DrkDialogCard>
                    <DialogContentText>
                        {confirmDialogMessage}
                    </DialogContentText>
                </DrkDialogCard>
            </DrkDialog>
        </DrkPageContainer>
    );
}

export default NotificationsPage;
